// ServiceCard.js
import React, { useMemo } from 'react';
import { Typography, Button, Chip, Box, IconButton, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import logoImage from '../../assets/images/logo.png';
import {
  ALL_TRADES,
  findTradeSubserviceWithGroup,
  getTradeTheme,
} from '../../constants/mockData/allTrades';
import {
  formatSubservicePricingLabel,
  normalizePricingForDisplay,
} from '../../utils/formatPrice';
import {
  StyledServiceCard,
  AccentBar,
  CardInner,
  BusinessHeader,
  BusinessLogo,
  BusinessName,
  ServiceTypeRow,
  SubserviceIconWrap,
  MetaChips,
  CardContentWrapper,
  ServiceTitle,
  GroupBlurb,
  PricingRow,
  DescriptionContainer,
  CTAButtons,
} from './ServiceCard.styled';

function titleCaseSlug(slug) {
  if (!slug || typeof slug !== 'string') return 'Service';
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Popper must sit above preview/config modals (z-index ~10000). */
const PREVIEW_TOOLTIP_Z_INDEX = 10100;

function PreviewFieldHint({ label }) {
  return (
    <Tooltip
      title={label}
      placement="top"
      arrow
      enterDelay={200}
      leaveDelay={0}
      slotProps={{
        popper: {
          sx: { zIndex: PREVIEW_TOOLTIP_Z_INDEX },
        },
      }}
    >
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
      >
        <IconButton
          size="small"
          aria-label={label}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          sx={{
            p: 0.25,
            ml: 0.25,
            color: '#0d4efe',
            opacity: 0.9,
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(13, 78, 254, 0.08)',
            },
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
    </Tooltip>
  );
}

const placeholderSx = {
  fontStyle: 'italic',
  color: '#94a3b8',
};

const ServiceCard = ({
  service,
  onBookService,
  onRequestQuote,
  /** When set, shows “Send inquiry” using the visitor’s saved service search (storefront wizard flow). */
  onWizardSearchInquiry,
  onCardClick,
  showActions = true,
  showBusinessHeader = true,
  /** When set, shows info icons with tooltips mapping card regions to form fields. */
  previewFieldLabels = null,
  /** Keys among productName, tagline, description — render those regions as placeholder styling. */
  previewPlaceholderKeys = [],
}) => {
  const isPreview = Boolean(previewFieldLabels);
  const placeholderSet = useMemo(
    () => new Set(previewPlaceholderKeys || []),
    [previewPlaceholderKeys],
  );
  const catalog = useMemo(() => {
    const tradeId = service._parentServiceType || service.ServiceType;
    if (!tradeId || !service.SubServiceType) {
      return {
        trade: null,
        match: null,
        accent: service.tradeAccentColor || service.catalogGroupColor || '#0d4efe',
      };
    }
    const trade = ALL_TRADES.find((t) => t.id === tradeId) || getTradeTheme(tradeId);
    const match = trade
      ? findTradeSubserviceWithGroup(trade, service.SubServiceType)
      : null;
    const accent =
      match?.group?.color ||
      service.catalogGroupColor ||
      trade?.color ||
      getTradeTheme(tradeId)?.color ||
      service.tradeAccentColor ||
      '#0d4efe';
    return { trade, match, accent };
  }, [service]);

  const groupName =
    service.catalogGroupName || catalog.match?.group?.name || null;
  const cardTagline =
    typeof service.Tagline === 'string' && service.Tagline.trim()
      ? service.Tagline.trim()
      : null;
  const groupBlurb =
    cardTagline ||
    service.catalogGroupDescription ||
    catalog.match?.group?.description ||
    null;
  const IconComponent =
    catalog.match?.subservice?.icon || catalog.trade?.icon || null;
  const tradeLabel =
    service.tradeLabel || catalog.trade?.name || titleCaseSlug(service.ServiceType);

  const handleBookService = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onBookService) {
      onBookService(service);
    }
  };

  const handleRequestQuote = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRequestQuote) {
      onRequestQuote(service);
    }
  };

  const handleWizardSearchInquiry = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onWizardSearchInquiry) {
      onWizardSearchInquiry(service);
    }
  };

  const handleCardClick = (e) => {
    if (e.target.closest('button')) {
      return;
    }
    if (onCardClick) {
      onCardClick(service);
    }
  };

  const pricingLabel = useMemo(() => {
    const normalized = normalizePricingForDisplay(service.Pricing);
    const label = formatSubservicePricingLabel(normalized);
    if (label !== 'Contact for pricing') return label;

    const pt = normalized?.PriceType;
    if (pt === 'Dynamic') return 'Quote on request';
    if (!normalized) return 'Pricing not available';

    return label;
  }, [service.Pricing]);
  const descriptionText =
    service.ProductDescription?.trim() ||
    catalog.match?.subservice?.description ||
    'No description available.';

  return (
    <StyledServiceCard
      onClick={isPreview ? undefined : handleCardClick}
      elevation={0}
      sx={
        isPreview
          ? { cursor: 'default', overflow: 'visible', '&:hover': { transform: 'none' } }
          : undefined
      }
    >
      <AccentBar sx={{ backgroundColor: catalog.accent }} />

      <CardInner>
        {showBusinessHeader && service.BusinessName && (
          <BusinessHeader>
            <BusinessLogo
              src={service.BusinessPhotoUrl || logoImage}
              alt={`${service.BusinessName} logo`}
              onError={(e) => {
                e.target.src = logoImage;
              }}
            />
            <BusinessName variant="body2">{service.BusinessName}</BusinessName>
          </BusinessHeader>
        )}

        <ServiceTypeRow>
          <SubserviceIconWrap
            sx={{
              backgroundColor: `${catalog.accent}18`,
              color: catalog.accent,
            }}
          >
            {IconComponent ? (
              <IconComponent sx={{ fontSize: 24 }} />
            ) : (
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 800,
                  lineHeight: 1,
                  color: 'inherit',
                }}
              >
                {(service.ProductName || tradeLabel || '?').charAt(0)}
              </Typography>
            )}
          </SubserviceIconWrap>
          <MetaChips>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              <Chip
                label={tradeLabel}
                size="small"
                sx={{
                  height: 24,
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  backgroundColor: `${catalog.accent}14`,
                  color: catalog.accent,
                  border: 'none',
                }}
              />
              {previewFieldLabels?.trade ? (
                <PreviewFieldHint label={previewFieldLabels.trade} />
              ) : null}
            </Box>
            {groupName ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                <Chip
                  label={groupName}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    borderColor: 'rgba(0,0,0,0.12)',
                    color: '#475569',
                  }}
                />
                {previewFieldLabels?.group ? (
                  <PreviewFieldHint label={previewFieldLabels.group} />
                ) : null}
              </Box>
            ) : null}
          </MetaChips>
        </ServiceTypeRow>

        <CardContentWrapper>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
            <ServiceTitle
              variant="h6"
              component="h3"
              sx={placeholderSet.has('productName') ? placeholderSx : undefined}
            >
              {service.ProductName || 'Service'}
            </ServiceTitle>
            {previewFieldLabels?.productName ? (
              <PreviewFieldHint label={previewFieldLabels.productName} />
            ) : null}
          </Box>
          {groupBlurb ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
              <GroupBlurb
                variant="body2"
                sx={placeholderSet.has('tagline') ? placeholderSx : undefined}
              >
                {groupBlurb}
              </GroupBlurb>
              {previewFieldLabels?.tagline ? (
                <PreviewFieldHint label={previewFieldLabels.tagline} />
              ) : null}
            </Box>
          ) : null}

          <PricingRow>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              <Chip
                label={pricingLabel}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  backgroundColor: '#f1f5f9',
                  color: '#0f172a',
                  height: 28,
                  borderRadius: '8px',
                }}
              />
              {previewFieldLabels?.pricing ? (
                <PreviewFieldHint label={previewFieldLabels.pricing} />
              ) : null}
            </Box>
            {service.FinancingAvailable && (
              <Chip
                label="Financing available"
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  backgroundColor: '#ecfdf5',
                  color: '#047857',
                  height: 28,
                  borderRadius: '8px',
                  ml: 0.75,
                }}
              />
            )}
          </PricingRow>

          <DescriptionContainer>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.55,
                  fontSize: '0.8125rem',
                  color: '#334155',
                  flex: 1,
                  ...(placeholderSet.has('description') ? placeholderSx : {}),
                }}
              >
                {descriptionText}
              </Typography>
              {previewFieldLabels?.description ? (
                <PreviewFieldHint label={previewFieldLabels.description} />
              ) : null}
            </Box>
          </DescriptionContainer>
        </CardContentWrapper>

        {showActions &&
          (service.IsSchedulable ||
            service.QuotingEnabled ||
            onWizardSearchInquiry) && (
            <CTAButtons>
              {service.IsSchedulable && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBookService}
                  fullWidth
                  sx={{ py: 1.1 }}
                >
                  Book service
                </Button>
              )}
              {service.QuotingEnabled && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleRequestQuote}
                  fullWidth
                  sx={{ py: 1.1 }}
                >
                  Instant quote
                </Button>
              )}
              {onWizardSearchInquiry && (
                <Button
                  variant={
                    service.IsSchedulable || service.QuotingEnabled
                      ? 'outlined'
                      : 'contained'
                  }
                  color="primary"
                  onClick={handleWizardSearchInquiry}
                  fullWidth
                  sx={{ py: 1.1 }}
                >
                  Send inquiry
                </Button>
              )}
            </CTAButtons>
          )}
      </CardInner>
    </StyledServiceCard>
  );
};

export default ServiceCard;
